// src/pages/IDCardGenerator/IDCardGenerator.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import "./IDCard.css";

const url = process.env.REACT_APP_BACKEND_URL;

const IDCardGenerator = () => {
  const [customers, setCustomers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [jobAvailabilities, setJobAvailabilities] = useState([]);
  const [clients, setClients] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [company, setCompany] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [customerPhoto, setCustomerPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);
  const [printType, setPrintType] = useState('');

  // Get absolute URLs for images in public folder
  const getImageUrl = (filename) => {
    return `${window.location.origin}/${filename}`;
  };

  // Get full photo URL - FIXED to properly handle paths
  const getFullPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    // If the path already starts with http, return as is
    if (photoPath.startsWith('http')) return photoPath;
    // If it already has /uploads/ prefix
    if (photoPath.startsWith('/uploads')) return `${url}${photoPath}`;
    // Otherwise, prepend /uploads/ and the backend URL
    return `${url}/uploads/${photoPath}`;
  };

  // Fetch all necessary data
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError("");
      
      console.log("🔍 Fetching data from:", url);
      
      // Fetch customers
      try {
        const customersRes = await axios.get(`${url}/api/customer/all`);
        console.log("✅ Customers fetched:", customersRes.data.data?.length || 0);
        setCustomers(customersRes.data.data || []);
      } catch (err) {
        console.error("❌ Error fetching customers:", err.response?.status, err.message);
        setError(`Failed to fetch customers: ${err.response?.data?.message || err.message}`);
        setCustomers([]);
      }
      
      // Fetch contracts
      try {
        const contractsRes = await axios.get(`${url}/api/contract/all`);
        console.log("✅ Contracts fetched:", contractsRes.data.data?.length || 0);
        setContracts(contractsRes.data.data || []);
      } catch (err) {
        console.error("❌ Error fetching contracts:", err.response?.status, err.message);
        setContracts([]);
      }
      
      // Fetch job availabilities
      try {
        const jobAvailRes = await axios.get(`${url}/api/jobavailabity/all`);
        console.log("✅ Job availabilities fetched:", jobAvailRes.data.data?.length || 0);
        setJobAvailabilities(jobAvailRes.data.data || []);
      } catch (err) {
        console.error("❌ Error fetching job availabilities:", err.response?.status, err.message);
        setJobAvailabilities([]);
      }
      
      // Fetch clients
      try {
        const clientsRes = await axios.get(`${url}/api/client/all`);
        console.log("✅ Clients fetched:", clientsRes.data.data?.length || 0);
        setClients(clientsRes.data.data || []);
      } catch (err) {
        console.error("❌ Error fetching clients:", err.response?.status, err.message);
        setClients([]);
      }
      
      // Fetch documents (for photos)
      try {
        const documentsRes = await axios.get(`${url}/api/documents/all`);
        console.log("✅ Documents fetched:", documentsRes.data.data?.length || 0);
        console.log("📸 Sample document:", documentsRes.data.data?.[0]);
        setDocuments(documentsRes.data.data || []);
      } catch (err) {
        console.error("❌ Error fetching documents:", err.response?.status, err.message);
        setDocuments([]);
      }
      
      // Fetch company
      try {
        const companyRes = await axios.get(`${url}/api/company/latest`);
        console.log("✅ Company fetched:", companyRes.data.data?.companyname || "N/A");
        setCompany(companyRes.data.data || null);
      } catch (err) {
        console.error("❌ Error fetching company:", err.response?.status, err.message);
        setCompany(null);
      }
      
    } catch (err) {
      console.error("❌ Overall error:", err);
      setError("Failed to fetch data. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get customer's photo from documents
  const getCustomerPhoto = (customerId) => {
    if (!documents || documents.length === 0) {
      console.log("No documents found");
      return null;
    }
    
    console.log(`Looking for photo for customer ID: ${customerId}`);
    console.log("Documents:", documents.map(d => ({ 
      id: d._id, 
      customer: d.customer?._id || d.customer,
      photos: d.photos 
    })));
    
    const customerDoc = documents.find(doc => {
      const docCustomerId = doc.customer?._id || doc.customer;
      return docCustomerId === customerId && doc.status === "active";
    });
    
    if (customerDoc) {
      console.log("✅ Found photo for customer:", customerDoc.photos);
      return customerDoc.photos;
    } else {
      console.log("❌ No photo found for customer ID:", customerId);
      return null;
    }
  };

  // Get customer's active contract
  const getCustomerContract = (customerId) => {
    if (!contracts || contracts.length === 0) return null;
    return contracts.find(c => {
      const custId = c.cust_id?._id || c.cust_id;
      return custId === customerId && c.status === "active";
    });
  };

  // Get customer's job availability
  const getCustomerJobAvailability = (customerId) => {
    if (!jobAvailabilities || jobAvailabilities.length === 0) return null;
    return jobAvailabilities.find(j => {
      const custId = j.customer?._id || j.customer;
      return custId === customerId && j.status === "active";
    });
  };

  // Get client by ID
  const getClientById = (clientId) => {
    if (!clients || clients.length === 0) return null;
    return clients.find(c => c._id === clientId);
  };

  // Get customer ID number (autoincrement based on registration order)
  const getCustomerIdNumber = (customer) => {
    if (!customer || !customers.length) return "N/A";
    const sortedCustomers = [...customers].sort((a, b) => new Date(a.date_reg || a.createdAt) - new Date(b.date_reg || b.createdAt));
    const index = sortedCustomers.findIndex(c => c._id === customer._id);
    return (index + 1).toString().padStart(4, '0');
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c._id === customerId);
    if (customer) {
      console.log("Selected customer:", customer.fullname);
      console.log("Customer ID:", customer._id);
      setSelectedCustomer(customer);
      const contract = getCustomerContract(customer._id);
      setSelectedContract(contract);
      const photo = getCustomerPhoto(customer._id);
      console.log("Photo found:", photo);
      setCustomerPhoto(photo);
      if (!contract) {
        console.log("No active contract found for this customer");
      }
    }
  };

  const handlePrint = (type) => {
    if (!selectedCustomer) return;
    
    setPrintType(type);
    setIsPrinting(true);
    
    setTimeout(() => {
      const printContent = generatePrintContent(type);
      openPrintWindow(printContent, type);
    }, 100);
  };

  const generatePrintContent = (type) => {
    if (!selectedCustomer) return '';

    const customerIdNo = getCustomerIdNumber(selectedCustomer);
    const jobAvail = getCustomerJobAvailability(selectedCustomer._id);
    const client = selectedContract ? getClientById(selectedContract.client_id?._id || selectedContract.client_id) : null;
    const photo = getCustomerPhoto(selectedCustomer._id);
    
    const contractStart = selectedContract?.contract_start ? new Date(selectedContract.contract_start).toLocaleDateString() : "N/A";
    const contractEnd = selectedContract?.contract_end ? new Date(selectedContract.contract_end).toLocaleDateString() : "N/A";

    // Construct full photo URL
    const photoUrl = getFullPhotoUrl(photo);
    console.log("Photo URL for print:", photoUrl);
    
    const photoHtml = photoUrl 
      ? `<img src="${photoUrl}" alt="Customer Photo" style="width:100%;height:100%;object-fit:cover;" onerror="this.style.display='none'; this.parentElement.innerHTML='<div class=\'photo-placeholder\'>No Photo</div>';" />`
      : '<div class="photo-placeholder">No Photo</div>';

    const frontContent = `
      <div class="id-card-print-front">
        <div class="id-card-frame" style="background-image: url('${getImageUrl('front.png')}')">
          <div class="overlay name">${escapeHtml(selectedCustomer.fullname || '')}</div>
          <div class="overlay idno">${escapeHtml(customerIdNo)}</div>
          <div class="overlay job-title">${escapeHtml(jobAvail?.jobname?.category_name || 'N/A')}</div>
          <div class="overlay client-name">${escapeHtml(client?.name || 'N/A')}</div>
          <div class="overlay issue-date">${escapeHtml(contractStart)}</div>
          <div class="overlay expire-date">${escapeHtml(contractEnd)}</div>
          <div class="overlay photo">
            ${photoHtml}
          </div>
        </div>
      </div>
    `;

    const backContent = `
      <div class="id-card-print-back">
        <div class="id-card-frame" style="background-image: url('${getImageUrl('back.png')}')">
          <div class="overlay qr">
            <div class="qr-code"></div>
          </div>
          ${company?.companyseal ? 
            `<div class="overlay seal">
              <img src="${getFullPhotoUrl(company.companyseal)}" alt="Company Seal" />
            </div>` : ''
          }
          ${company?.companysignature ? 
            `<div class="overlay signature">
              <img src="${getFullPhotoUrl(company.companysignature)}" alt="Company Signature" />
            </div>` : ''
          }
        </div>
      </div>
    `;

    switch (type) {
      case 'front':
        return frontContent;
      case 'back':
        return backContent;
      case 'both':
        return frontContent + backContent;
      default:
        return '';
    }
  };

  const escapeHtml = (text) => {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const openPrintWindow = (content, type) => {
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) {
      alert('Please allow popups for printing');
      setIsPrinting(false);
      return;
    }

    const jobAvail = getCustomerJobAvailability(selectedCustomer._id);
    const client = selectedContract ? getClientById(selectedContract.client_id?._id || selectedContract.client_id) : null;
    
    const qrData = {
      customer_id: selectedCustomer._id,
      id_number: getCustomerIdNumber(selectedCustomer),
      fullname: selectedCustomer.fullname,
      job: jobAvail?.jobname?.category_name || "N/A",
      client: client?.name || "N/A",
      contract_start: selectedContract?.contract_start ? new Date(selectedContract.contract_start).toLocaleDateString() : "N/A",
      contract_end: selectedContract?.contract_end ? new Date(selectedContract.contract_end).toLocaleDateString() : "N/A"
    };

    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${selectedCustomer.fullname} - ID Card ${type}</title>
          <style>
            ${getPrintStyles()}
          </style>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcode-generator/1.4.4/qrcode.min.js"></script>
        </head>
        <body>
          <div class="print-container">
            ${content}
          </div>
          <script>
            function generateQRCode(text, container) {
              try {
                const typeNumber = 0;
                const errorCorrectionLevel = 'L';
                const qr = qrcode(typeNumber, errorCorrectionLevel);
                qr.addData(text);
                qr.make();
                
                const qrSize = 120;
                const cellSize = Math.floor(qrSize / qr.getModuleCount());
                const margin = 0;
                const size = qr.getModuleCount() * cellSize + margin * 2;
                
                let svg = '<svg width="' + qrSize + '" height="' + qrSize + '" viewBox="0 0 ' + size + ' ' + size + '">';
                svg += '<rect width="100%" height="100%" fill="white"/>';
                
                for (let row = 0; row < qr.getModuleCount(); row++) {
                  for (let col = 0; col < qr.getModuleCount(); col++) {
                    if (qr.isDark(row, col)) {
                      svg += '<rect x="' + (col * cellSize + margin) + '" y="' + (row * cellSize + margin) + '" width="' + cellSize + '" height="' + cellSize + '" fill="black"/>';
                    }
                  }
                }
                
                svg += '</svg>';
                container.innerHTML = svg;
              } catch (e) {
                console.error('QR Code generation failed:', e);
                container.innerHTML = '<div style="width:120px;height:120px;background:#f0f0f0;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;font-size:10px;text-align:center;padding:5px;">QR Code</div>';
              }
            }

            document.addEventListener('DOMContentLoaded', function() {
              const qrElements = document.querySelectorAll('.qr-code');
              if (qrElements.length > 0) {
                const qrText = ${JSON.stringify(JSON.stringify(qrData, null, 2))};
                qrElements.forEach(element => {
                  generateQRCode(qrText, element);
                });
              }

              setTimeout(() => {
                window.print();
                setTimeout(() => {
                  window.close();
                }, 500);
              }, 100);
            });

            window.onafterprint = function() {
              setTimeout(() => {
                window.close();
              }, 1000);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(printDocument);
    printWindow.document.close();
    
    printWindow.onbeforeunload = () => {
      setIsPrinting(false);
    };
  };

  const getPrintStyles = () => `
    @media print {
      @page {
        margin: 0;
        size: 1012px 638px;
      }
      
      body, html {
        margin: 0 !important;
        padding: 0 !important;
        width: 1012px !important;
        height: 638px !important;
      }
    }
    
    body {
      margin: 0;
      padding: 0;
      background: white;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      font-family: Arial, sans-serif;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .print-container {
      display: flex;
      flex-direction: column;
      gap: 0;
    }
    
    .id-card-print-front,
    .id-card-print-back {
      width: 1012px;
      height: 638px;
      page-break-after: always;
      position: relative;
    }
    
    .id-card-frame {
      width: 1012px !important;
      height: 638px !important;
      background-size: 1012px 638px !important;
      background-position: center !important;
      background-repeat: no-repeat !important;
      position: relative !important;
      background-color: white;
    }
    
    .overlay {
      position: absolute;
      color: black;
      margin: 0;
      padding: 0;
    }
    
    .name { 
      top: 270px; 
      left: 220px; 
      font-size: 28px; 
      font-weight: bold;
      font-family: Arial, sans-serif;
    }
    
    .idno { 
      top: 465px; 
      left: 905px; 
      font-size: 20px; 
      font-weight: bold;
      color: #2c3e50;
    }
    
    .job-title { 
      top: 370px; 
      left: 220px; 
      font-size: 20px; 
      font-weight: 500;
    }
    
    .client-name { 
      top: 460px; 
      left: 220px; 
      font-size: 18px; 
      font-weight: 500;
    }
    
    .issue-date { 
      top: 600px; 
      left: 200px; 
      font-size: 14px; 
    }
    
    .expire-date { 
      top: 600px; 
      left: 600px; 
      font-size: 14px; 
    }
    
    .photo {
      position: absolute;
      top: 230px;
      right: 50px;
      width: 220px;
      height: 200px;
      overflow: hidden;
      background: #f5f5f5;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #ddd;
      border-radius: 8px;
    }
    
    .photo img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .photo-placeholder {
      color: #999;
      font-size: 14px;
      text-align: center;
    }
    
    .qr { 
      position: absolute; 
      top: 210px; 
      left: 80px; 
      background: white;
      padding: 10px;
      border: 1px solid #ddd;
      border-radius: 8px;
    }
    
    .qr-code {
      width: 120px;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: white;
    }
    
    .qr-code svg {
      width: 100%;
      height: 100%;
    }
    
    .seal {
      position: absolute;
      top: 210px;
      right: 400px;
      width: 120px;
      height: 120px;
      background: white;
      padding: 5px;
      border: 1px solid #ddd;
      border-radius: 8px;
    }
    
    .seal img { 
      width: 100%; 
      height: 100%; 
      object-fit: contain; 
    }
    
    .signature {
      position: absolute;
      top: 230px;
      right: 60px;
      width: 120px;
      height: 80px;
      background: white;
      padding: 5px;
      border: 1px solid #ddd;
      border-radius: 8px;
    }
    
    .signature img { 
      width: 100%; 
      height: 100%; 
      object-fit: contain; 
    }
  `;

  if (loading) return <div className="loading">Loading data...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="id-card-generator">
      <h1>Customer ID Card Generator</h1>

      {/* Customer Selector */}
      <div className="customer-selector">
        <label htmlFor="customer-select">Select Customer: </label>
        <select
          id="customer-select"
          value={selectedCustomer?._id || ""}
          onChange={(e) => handleCustomerSelect(e.target.value)}
        >
          <option value="">-- Select a customer --</option>
          {customers.map((customer) => (
            <option key={customer._id} value={customer._id}>
              {customer.fullname} (ID: {getCustomerIdNumber(customer)})
            </option>
          ))}
        </select>
      </div>

      {/* Preview Section */}
      {selectedCustomer && (
        <div className="preview-section">
          <h2>Preview for {selectedCustomer.fullname}</h2>
          
          {/* Preview Cards */}
          <div className="cards-container">
            <div className="id-card-front">
              <div className="id-card-frame" style={{ backgroundImage: `url(${getImageUrl('front.png')})` }}>
                <div className="overlay name">{selectedCustomer.fullname}</div>
                <div className="overlay idno">{getCustomerIdNumber(selectedCustomer)}</div>
                <div className="overlay job-title">
                  {getCustomerJobAvailability(selectedCustomer._id)?.jobname?.category_name || 'N/A'}
                </div>
                <div className="overlay client-name">
                  {selectedContract ? getClientById(selectedContract.client_id?._id || selectedContract.client_id)?.name : 'No Active Contract'}
                </div>
                <div className="overlay issue-date">
                  {selectedContract?.contract_start ? new Date(selectedContract.contract_start).toLocaleDateString() : 'N/A'}
                </div>
                <div className="overlay expire-date">
                  {selectedContract?.contract_end ? new Date(selectedContract.contract_end).toLocaleDateString() : 'N/A'}
                </div>
                <div className="overlay photo">
                  {customerPhoto ? (
                    <img 
                      src={getFullPhotoUrl(customerPhoto)} 
                      alt="Customer" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        console.error("Failed to load photo:", getFullPhotoUrl(customerPhoto));
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="photo-placeholder">No Photo</div>';
                      }}
                    />
                  ) : (
                    <div className="photo-placeholder">No Photo</div>
                  )}
                </div>
              </div>
            </div>

            <div className="id-card-back">
              <div className="id-card-frame" style={{ backgroundImage: `url(${getImageUrl('back.png')})` }}>
                <div className="overlay qr">
                  <QRCodeCanvas
                    value={JSON.stringify({
                      customer_id: selectedCustomer._id,
                      id_number: getCustomerIdNumber(selectedCustomer),
                      fullname: selectedCustomer.fullname,
                      job: getCustomerJobAvailability(selectedCustomer._id)?.jobname?.category_name || "N/A",
                      client: selectedContract ? getClientById(selectedContract.client_id?._id || selectedContract.client_id)?.name : "N/A",
                      contract_start: selectedContract?.contract_start ? new Date(selectedContract.contract_start).toLocaleDateString() : "N/A",
                      contract_end: selectedContract?.contract_end ? new Date(selectedContract.contract_end).toLocaleDateString() : "N/A"
                    })}
                    size={120}
                  />
                </div>
                {company?.companyseal && (
                  <div className="overlay seal">
                    <img src={getFullPhotoUrl(company.companyseal)} alt="Company Seal" />
                  </div>
                )}
                {company?.companysignature && (
                  <div className="overlay signature">
                    <img src={getFullPhotoUrl(company.companysignature)} alt="Company Signature" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Print Buttons */}
          <div className="button-row">
            <button 
              onClick={() => handlePrint('front')} 
              disabled={!selectedCustomer || isPrinting}
            >
              {isPrinting && printType === 'front' ? 'Printing...' : 'Print Front'}
            </button>
            <button 
              onClick={() => handlePrint('back')} 
              disabled={!selectedCustomer || isPrinting}
            >
              {isPrinting && printType === 'back' ? 'Printing...' : 'Print Back'}
            </button>
          </div>
        </div>
      )}

      {!selectedCustomer && customers.length > 0 && (
        <div className="no-selection">
          <p>Please select a customer to generate ID card</p>
        </div>
      )}

      {customers.length === 0 && !loading && (
        <div className="no-selection">
          <p>No customers found. Please add customers first.</p>
        </div>
      )}
    </div>
  );
};

export default IDCardGenerator;